import { Stack, TextField, Typography } from '@mui/material'
import React from 'react'
import { CheckCorrectTimeFormat } from '../../generic/DateTimeFunctions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { isExternalEffective } from './jobExternalCharacter'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference
 */

/**
 * Acordeón de Preparación: muestra y permite editar los campos de preparación
 * inicial (initialPreparation) e intermedia (intermediatePreparation) de un jobReference.
 *
 * @param {Object} props
 * @param {JobReference} props.jobReference
 * @param {boolean} props.editing
 * @param {function(JobReference)} props.updateJobReference
 */
const NodePreparation = ({ jobReference, editing, updateJobReference }) => {
  const { t_ } = useSmartTranslation()
  // HU-03 (S62/S63/RN25): four of the five fields of this story live in this block. The trigger is THE
  // MARK, and only the mark (RN26) — the technology's type plays no part, so a marked JOB on an INTERNAL
  // technology disables these fields just the same (S66). This block itself neither clears nor normalises
  // anything: the emptying happens once, when the mark goes on, in the checkbox's own handler (fix #2885,
  // `jobExternalReset.js`). Unmarking brings nothing back — the values stay at zero until re-entered.
  const externallyEffective = isExternalEffective(jobReference)
  const fieldsDisabled = !editing || externallyEffective

  const initialPreparation = jobReference?.jobPreparation?.initialPreparation ?? { time: '00:00:00', operators: '' }
  const intermediatePreparation = jobReference?.jobPreparation?.intermediatePreparation ?? { time: '00:00:00', operators: '' }

  const updateInitialPreparation = (field, value) => {
    const currentPrep = jobReference?.jobPreparation ?? {
      initialPreparation: { time: '00:00:00', operators: '' },
      intermediatePreparation: { time: '00:00:00', operators: '' }
    }
    updateJobReference({
      ...jobReference,
      jobPreparation: {
        ...currentPrep,
        initialPreparation: { ...currentPrep.initialPreparation, [field]: value }
      }
    })
  }

  const updateIntermediatePreparation = (field, value) => {
    const currentPrep = jobReference?.jobPreparation ?? {
      initialPreparation: { time: '00:00:00', operators: '' },
      intermediatePreparation: { time: '00:00:00', operators: '' }
    }
    updateJobReference({
      ...jobReference,
      jobPreparation: {
        ...currentPrep,
        intermediatePreparation: { ...currentPrep.intermediatePreparation, [field]: value }
      }
    })
  }

  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
        <Typography color='primary' noWrap>{t_('Preparation time') + ':'}</Typography>
        <TextField
          label={t_('First part')}
          type='text'
          value={initialPreparation.time}
          inputProps={{ 'data-testid': 'prep-time-first-part' }}
          disabled={fieldsDisabled}
          size='small'
          sx={{ width: 100 }}
          error={CheckCorrectTimeFormat(initialPreparation.time || '', t_)}
          onChange={(event) => updateInitialPreparation('time', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t_('Next parts')}
          type='text'
          value={intermediatePreparation.time}
          inputProps={{ 'data-testid': 'prep-time-next-parts' }}
          disabled={fieldsDisabled}
          size='small'
          sx={{ width: 100 }}
          error={CheckCorrectTimeFormat(intermediatePreparation.time || '', t_)}
          onChange={(event) => updateIntermediatePreparation('time', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography color='primary' noWrap>{t_('Operators') + ':'}</Typography>
        <TextField
          type='number'
          value={initialPreparation.operators === '' ? '' : initialPreparation.operators}
          inputProps={{ min: 0, 'data-testid': 'op-first-part' }}
          disabled={fieldsDisabled}
          sx={{ width: 130 }}
          label={t_('First part')}
          onChange={(event) => updateInitialPreparation('operators', event.target.value)}
          InputLabelProps={{ shrink: true }}
          size='small'
        />
        <TextField
          type='number'
          value={intermediatePreparation.operators === '' ? '' : intermediatePreparation.operators}
          inputProps={{ min: 0, 'data-testid': 'op-next-parts' }}
          disabled={fieldsDisabled}
          sx={{ width: 130 }}
          label={t_('Next parts')}
          onChange={(event) => updateIntermediatePreparation('operators', event.target.value)}
          InputLabelProps={{ shrink: true }}
          size='small'
        />
      </Stack>
    </Stack>
  )
}

export default NodePreparation
